import React, { useMemo, useState } from 'react';
/**
 * RulebookHintsPanel — Family-hints verkenner (P1-4).
 *
 * Doel: één plek waar een redacteur per family ziet wat de rulebooks
 * voorschrijven aan vraagtypes (familyTypeHints) én visuals
 * (familyVisualHints). Dit is de inverse van LiveRulebookCard, dat per
 * module toont. Hier kies je de family en ziet je alle matchende hints
 * vanuit elk relevant rulebook — een 1-blik check voor
 * "past mijn vraag bij deze family?".
 *
 * Gevoed door:
 *   - QUESTIONS_BY_FAMILY (alle bestaande families)
 *   - resolveFamilyTypeHints() / resolveFamilyVisualHints() (regelboeken)
 *   - getModuleRulebook() voor de module-context van de geselecteerde family
 *   - FAMILY_DISPLAY_NAMES voor mensvriendelijke labels
 */
import { TypeIcon, PaletteIcon, CheckCircle2Icon, XCircleIcon, SearchIcon, FilterIcon, InfoIcon } from 'lucide-react';
import { Input } from '../../../../components/ui/Input';
import { Select } from '../../../../components/ui/Select';
import { QUESTIONS_BY_FAMILY, FAMILY_DISPLAY_NAMES } from '../../../../content';
import { resolveFamilyTypeHints, resolveFamilyVisualHints, getModuleRulebook } from '../../../../governance';
import { ADMIN_MODULE_META } from '../../shared/moduleMetadata';
type FamilyRow = {
  familyId: string;
  displayName: string;
  shortCode: string;
  moduleName: string;
  questionCount: number;
  hasTypeHint: boolean;
  hasVisualHint: boolean;
};
export function RulebookHintsPanel() {
  const [search, setSearch] = useState('');
  const [moduleFilter, setModuleFilter] = useState<string>('all');
  const [hintFilter, setHintFilter] = useState<'all' | 'with-type' | 'with-visual' | 'without-any'>('all');
  const [selectedFamilyId, setSelectedFamilyId] = useState<string | null>(null);
  // Build the family rows once — we resolve hints upfront so we can
  // filter "with/without hint" without recomputing on each render.
  const rows = useMemo<FamilyRow[]>(() => {
    const out: FamilyRow[] = [];
    for (const [familyId, questions] of QUESTIONS_BY_FAMILY.entries()) {
      const shortCode = familyId.split('-')[0] || 'UNK';
      const meta = ADMIN_MODULE_META[shortCode];
      const typeHints = resolveFamilyTypeHints(familyId);
      const visualHints = resolveFamilyVisualHints(familyId);
      out.push({
        familyId,
        displayName: FAMILY_DISPLAY_NAMES[familyId] ?? familyId,
        shortCode,
        moduleName: meta?.name ?? shortCode,
        questionCount: questions.length,
        hasTypeHint: typeHints.length > 0,
        hasVisualHint: visualHints.length > 0
      });
    }
    return out.sort((a, b) => a.familyId.localeCompare(b.familyId));
  }, []);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter(r => {
      if (moduleFilter !== 'all' && r.shortCode !== moduleFilter) return false;
      if (hintFilter === 'with-type' && !r.hasTypeHint) return false;
      if (hintFilter === 'with-visual' && !r.hasVisualHint) return false;
      if (hintFilter === 'without-any' && (r.hasTypeHint || r.hasVisualHint)) return false;
      if (q !== '') {
        const hay = r.familyId.toLowerCase() + ' ' + r.displayName.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [rows, search, moduleFilter, hintFilter]);
  // Auto-select first family on first render to avoid empty detail panel.
  const effectiveSelected = selectedFamilyId ?? filteredRows[0]?.familyId ?? null;
  const stats = useMemo(() => {
    const withType = rows.filter(r => r.hasTypeHint).length;
    const withVisual = rows.filter(r => r.hasVisualHint).length;
    const withAny = rows.filter(r => r.hasTypeHint || r.hasVisualHint).length;
    return {
      total: rows.length,
      withType,
      withVisual,
      withAny,
      coverage: rows.length === 0 ? 0 : Math.round(withAny / rows.length * 100)
    };
  }, [rows]);
  return <div className="space-y-5" data-id="element-4591">
      {/* Header card */}
      <div className="rounded-lg border border-indigo-100 bg-indigo-50 p-4" data-id="element-4592">
        <p className="text-sm text-indigo-900" data-id="element-4593">
          <strong data-id="element-4594">Family-hints verkenner</strong> — kies een family om te zien
          welke vraagtypes en visuals de regelboeken voorschrijven of afraden.
          Komt rechtstreeks uit{' '}
          <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4595">
            familyTypeHints
          </code>{' '}
          en{' '}
          <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4596">
            familyVisualHints
          </code>{' '}
          in{' '}
          <code className="font-mono bg-white/60 px-1 rounded" data-id="element-4597">
            src/governance/rulebooks/
          </code>
          .
        </p>
      </div>

      {/* Coverage tiles */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-id="element-4598">
        <CoverageTile label="Families totaal" value={stats.total} accent="slate" data-id="element-4599" />
        <CoverageTile label="Met type-hint" value={stats.withType} accent="purple" data-id="element-4600" />
        <CoverageTile label="Met visual-hint" value={stats.withVisual} accent="emerald" data-id="element-4601" />
        <CoverageTile label="Dekking" value={`${stats.coverage}%`} accent="indigo" data-id="element-4602" />
      </div>

      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row" data-id="element-4603">
        <div className="flex-1" data-id="element-4604">
          <Input type="text" placeholder="Zoek op family ID of naam..." value={search} onChange={e => {
          setSearch(e.target.value);
          setSelectedFamilyId(null);
        }} icon={<SearchIcon className="w-4 h-4" data-id="element-4606" />} variant="admin" size="sm" data-id="element-4605" />
        </div>
        <Select value={moduleFilter} onChange={e => {
        setModuleFilter(e.target.value);
        setSelectedFamilyId(null);
      }} options={[{
        value: 'all',
        label: 'Alle vakken'
      }, ...Object.entries(ADMIN_MODULE_META).map(([code, meta]) => ({
        value: code,
        label: `${meta.name} (${code})`
      }))]} variant="admin" size="sm" fullWidth={false} data-id="element-4607" />
        <Select value={hintFilter} onChange={e => {
        setHintFilter(e.target.value as typeof hintFilter);
        setSelectedFamilyId(null);
      }} options={[{
        value: 'all',
        label: 'Alle families'
      }, {
        value: 'with-type',
        label: 'Met type-hint'
      }, {
        value: 'with-visual',
        label: 'Met visual-hint'
      }, {
        value: 'without-any',
        label: 'Zonder hint'
      }]} variant="admin" size="sm" fullWidth={false} data-id="element-4608" />
      </div>

      <p className="text-sm text-gray-500 flex items-center gap-2" data-id="element-4609">
        <FilterIcon className="h-3.5 w-3.5" data-id="element-4610" />
        {filteredRows.length} van {rows.length} families
      </p>

      {/* Two-pane layout: family list + detail */}
      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4" data-id="element-4611">
        {/* Family list */}
        <div className="rounded-xl border border-gray-100 bg-white overflow-hidden max-h-[640px] overflow-y-auto" data-id="element-4612">
          {filteredRows.length === 0 ? <div className="py-12 text-center text-sm text-gray-500" data-id="element-4613">
              <SearchIcon className="mx-auto h-7 w-7 text-gray-300 mb-2" data-id="element-4614" />
              Geen families gevonden.
            </div> : <ul role="list" data-id="element-4615">
              {filteredRows.map(row => {
            const isSelected = row.familyId === effectiveSelected;
            return <li key={row.familyId} data-id="element-4616">
                    <button onClick={() => setSelectedFamilyId(row.familyId)} className={`w-full text-left px-3 py-2.5 border-b border-gray-50 transition-colors ${isSelected ? 'bg-indigo-50 border-l-2 border-l-indigo-500' : 'hover:bg-gray-50 border-l-2 border-l-transparent'}`} data-id="element-4617">
                      <div className="flex items-center justify-between gap-2" data-id="element-4618">
                        <code className={`text-xs font-mono font-semibold truncate ${isSelected ? 'text-indigo-700' : 'text-gray-800'}`} data-id="element-4619">
                          {row.familyId}
                        </code>
                        <div className="flex gap-1 flex-shrink-0" data-id="element-4620">
                          {row.hasTypeHint && <span title="Heeft type-hint" className="inline-block w-1.5 h-1.5 rounded-full bg-purple-500" data-id="element-4621" />}
                          {row.hasVisualHint && <span title="Heeft visual-hint" className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-4622" />}
                        </div>
                      </div>
                      <p className={`text-xs mt-0.5 truncate ${isSelected ? 'text-indigo-600' : 'text-gray-500'}`} data-id="element-4623">
                        {row.displayName}
                      </p>
                      <p className="text-[10px] text-gray-400 mt-0.5" data-id="element-4624">
                        {row.shortCode} · {row.questionCount} vragen
                      </p>
                    </button>
                  </li>;
          })}
            </ul>}
        </div>

        {/* Detail panel */}
        <div className="min-h-[400px]" data-id="element-4625">
          {effectiveSelected ? <FamilyHintsDetail familyId={effectiveSelected} data-id="element-4626" /> : <div className="rounded-xl border border-dashed border-gray-200 bg-gray-50 py-16 text-center" data-id="element-4627">
              <InfoIcon className="mx-auto h-7 w-7 text-gray-300 mb-2" data-id="element-4628" />
              <p className="text-sm text-gray-500" data-id="element-4629">
                Selecteer een family om de hints te zien.
              </p>
            </div>}
        </div>
      </div>
    </div>;
}
// ─── Detail panel ───────────────────────────────────────────────────────────
function FamilyHintsDetail({
  familyId
}: {
  familyId: string;
}) {
  const typeHints = resolveFamilyTypeHints(familyId);
  const visualHints = resolveFamilyVisualHints(familyId);
  const shortCode = familyId.split('-')[0] || '';
  const moduleMeta = ADMIN_MODULE_META[shortCode];
  // Find parent rulebook for module-level preferred types/visuals as fallback.
  const parentModuleId = typeHints[0]?.moduleId ?? visualHints[0]?.moduleId;
  const parentRulebook = parentModuleId ? getModuleRulebook(parentModuleId) : null;
  return <div className="rounded-xl border border-gray-100 bg-white overflow-hidden" data-id="element-4630">
      {/* Family header */}
      <div className="px-5 py-4 border-b border-gray-100 bg-gradient-to-br from-gray-50 to-white" data-id="element-4631">
        <div className="flex items-center gap-2 mb-1" data-id="element-4632">
          <code className="text-base font-mono font-bold text-gray-900" data-id="element-4633">
            {familyId}
          </code>
          {moduleMeta && <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold text-white ${moduleMeta.color}`} data-id="element-4634">
              {shortCode}
            </span>}
        </div>
        <p className="text-sm text-gray-600" data-id="element-4635">
          {FAMILY_DISPLAY_NAMES[familyId] ?? '—'}
        </p>
        {parentRulebook && <p className="text-xs text-gray-500 mt-1.5" data-id="element-4636">
            Rulebook: <strong data-id="element-4637">{parentRulebook.displayName}</strong> ·{' '}
            {parentRulebook.didactics.coreSummary}
          </p>}
      </div>

      <div className="p-5 space-y-5" data-id="element-4638">
        {/* Type hints */}
        <Section icon={<TypeIcon className="h-4 w-4 text-purple-600" data-id="element-4640" />} title="Vraagtype-hints" count={typeHints.length} data-id="element-4639">
          {typeHints.length === 0 ? <EmptyHint kind="vraagtype" data-id="element-4641" /> : <ul className="space-y-3" data-id="element-4642">
              {typeHints.map((hint, i) => <li key={`${hint.moduleId}-${i}`} className="rounded-lg border border-purple-100 bg-purple-50/40 p-3" data-id="element-4643">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-4644">
                    <code className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded border border-purple-100 text-purple-700" data-id="element-4645">
                      {hint.familyIdPattern}
                    </code>
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide" data-id="element-4646">
                      {hint.moduleId}
                    </span>
                  </div>
                  {hint.preferredTypes && hint.preferredTypes.length > 0 && <ChipRow label="Voorkeur" labelIcon={<CheckCircle2Icon className="h-3 w-3 text-emerald-600" data-id="element-4648" />} chips={hint.preferredTypes} chipClass="bg-emerald-50 text-emerald-700 border-emerald-100" data-id="element-4647" />}
                  {hint.discouragedTypes && hint.discouragedTypes.length > 0 && <ChipRow label="Afgeraden" labelIcon={<XCircleIcon className="h-3 w-3 text-red-500" data-id="element-4650" />} chips={hint.discouragedTypes} chipClass="bg-red-50 text-red-700 border-red-100 line-through decoration-red-300" data-id="element-4649" />}
                  <p className="text-xs text-gray-600 mt-1.5 leading-relaxed" data-id="element-4651">
                    {hint.note}
                  </p>
                </li>)}
            </ul>}
        </Section>

        {/* Visual hints */}
        <Section icon={<PaletteIcon className="h-4 w-4 text-emerald-600" data-id="element-4653" />} title="Visual-hints" count={visualHints.length} data-id="element-4652">
          {visualHints.length === 0 ? <EmptyHint kind="visual" data-id="element-4654" /> : <ul className="space-y-3" data-id="element-4655">
              {visualHints.map((hint, i) => <li key={`${hint.moduleId}-${i}`} className="rounded-lg border border-emerald-100 bg-emerald-50/40 p-3" data-id="element-4656">
                  <div className="flex items-center gap-2 mb-1.5 flex-wrap" data-id="element-4657">
                    <code className="font-mono text-[10px] bg-white px-1.5 py-0.5 rounded border border-emerald-100 text-emerald-700" data-id="element-4658">
                      {hint.familyIdPattern}
                    </code>
                    <span className="text-[10px] text-gray-500 uppercase tracking-wide" data-id="element-4659">
                      {hint.moduleId}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1.5 mb-1.5" data-id="element-4660">
                    {hint.preferredStimulus && <span className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 text-emerald-800 px-2 py-0.5 rounded" data-id="element-4661">
                        <span className="text-[9px] uppercase tracking-wider text-emerald-600" data-id="element-4662">
                          Stimulus
                        </span>
                        <code className="font-mono text-[11px]" data-id="element-4663">
                          {hint.preferredStimulus}
                        </code>
                      </span>}
                    {hint.preferredSupport && <span className="inline-flex items-center gap-1 text-xs bg-white border border-emerald-200 text-emerald-800 px-2 py-0.5 rounded" data-id="element-4664">
                        <span className="text-[9px] uppercase tracking-wider text-emerald-600" data-id="element-4665">
                          Support
                        </span>
                        <code className="font-mono text-[11px]" data-id="element-4666">
                          {hint.preferredSupport}
                        </code>
                      </span>}
                  </div>
                  {hint.questionTypeExempt && hint.questionTypeExempt.length > 0 && <p className="text-[11px] text-gray-500 italic mb-1" data-id="element-4667">
                        Uitgezonderd voor:{' '}
                        {hint.questionTypeExempt.map((t, j) => <code key={j} className="font-mono bg-gray-100 px-1 rounded mr-1" data-id="element-4668">
                            {t}
                          </code>)}
                      </p>}
                  <p className="text-xs text-gray-600 leading-relaxed" data-id="element-4669">
                    {hint.note}
                  </p>
                </li>)}
            </ul>}
        </Section>

        {/* Module-level fallback */}
        {parentRulebook && <div className="rounded-lg border border-gray-100 bg-gray-50 p-3" data-id="element-4670">
            <p className="text-[10px] uppercase tracking-wide text-gray-500 font-semibold mb-2 flex items-center gap-1.5" data-id="element-4671">
              <InfoIcon className="h-3 w-3" data-id="element-4672" />
              Module-fallback ({parentRulebook.displayName})
            </p>
            <div className="space-y-2 text-xs" data-id="element-4673">
              {parentRulebook.questionTypes.preferredTypes.length > 0 && <div className="flex flex-wrap gap-1.5 items-center" data-id="element-4674">
                  <span className="text-gray-500" data-id="element-4675">Voorkeur vraagtypes:</span>
                  {parentRulebook.questionTypes.preferredTypes.map(t => <code key={t} className="font-mono bg-white border border-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px]" data-id="element-4676">
                      {t}
                    </code>)}
                </div>}
              {parentRulebook.visuals.preferredAidTypes && parentRulebook.visuals.preferredAidTypes.length > 0 && <div className="flex flex-wrap gap-1.5 items-center" data-id="element-4677">
                    <span className="text-gray-500" data-id="element-4678">Voorkeur visuals:</span>
                    {parentRulebook.visuals.preferredAidTypes.map(v => <code key={v} className="font-mono bg-white border border-gray-200 text-gray-700 px-1.5 py-0.5 rounded text-[10px]" data-id="element-4679">
                        {v}
                      </code>)}
                  </div>}
            </div>
          </div>}
      </div>
    </div>;
}
// ─── Small UI primitives ────────────────────────────────────────────────────
function Section({
  icon,
  title,
  count,
  children
}: {
  icon: React.ReactNode;
  title: string;
  count: number;
  children: React.ReactNode;
}) {
  return <div data-id="element-4680">
      <div className="flex items-center gap-2 mb-2" data-id="element-4681">
        {icon}
        <h4 className="text-sm font-semibold text-gray-800" data-id="element-4682">{title}</h4>
        <span className="text-xs text-gray-400 font-medium" data-id="element-4683">({count})</span>
      </div>
      {children}
    </div>;
}
function ChipRow({
  label,
  labelIcon,
  chips,
  chipClass
}: {
  label: string;
  labelIcon: React.ReactNode;
  chips: string[];
  chipClass: string;
}) {
  return <div className="flex flex-wrap items-center gap-1.5 mb-1" data-id="element-4684">
      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500 font-semibold" data-id="element-4685">
        {labelIcon}
        {label}
      </span>
      {chips.map(c => <code key={c} className={`font-mono text-[11px] px-1.5 py-0.5 rounded border ${chipClass}`} data-id="element-4686">
          {c}
        </code>)}
    </div>;
}
function EmptyHint({
  kind
}: {
  kind: 'vraagtype' | 'visual';
}) {
  return <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50/50 p-4 text-center" data-id="element-4687">
      <p className="text-xs text-gray-500" data-id="element-4688">
        Geen {kind}-hint gedefinieerd voor deze family.
      </p>
      <p className="text-[11px] text-gray-400 mt-1" data-id="element-4689">
        De module-fallback hieronder geldt.
      </p>
    </div>;
}
function CoverageTile({
  label,
  value,
  accent
}: {
  label: string;
  value: number | string;
  accent: 'slate' | 'purple' | 'emerald' | 'indigo';
}) {
  const map = {
    slate: 'bg-slate-50 border-slate-100 text-slate-700',
    purple: 'bg-purple-50 border-purple-100 text-purple-700',
    emerald: 'bg-emerald-50 border-emerald-100 text-emerald-700',
    indigo: 'bg-indigo-50 border-indigo-100 text-indigo-700'
  } as const;
  return <div className={`rounded-lg border p-3 text-center ${map[accent]}`} data-id="element-4690">
      <p className="text-2xl font-bold" data-id="element-4691">{value}</p>
      <p className="text-xs font-medium opacity-80" data-id="element-4692">{label}</p>
    </div>;
}